import * as React from "react";
import { useTranslation } from "react-i18next";

import { Switch } from "@/components/_atoms/switch";
// --- Icons ---
import { ExternalLinkIcon } from "@/components/_organisms/tiptap/tiptap-icons/external-link-icon";
import { LinkIcon } from "@/components/_organisms/tiptap/tiptap-icons/link-icon";
import { TrashIcon } from "@/components/_organisms/tiptap/tiptap-icons/trash-icon";
// --- UI Primitives ---
import { Button, ButtonProps, ButtonSize } from "@/components/_organisms/tiptap/tiptap-ui-primitive/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/_organisms/tiptap/tiptap-ui-primitive/popover";
// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";
// --- Lib ---
import { isMarkInSchema } from "@/lib/tiptap-utils";
import { type Editor, isNodeSelection } from "@tiptap/react";

// --- Styles ---
import "@/components/_organisms/tiptap/tiptap-ui/link-popover/link-popover.scss";

export interface LinkHandlerProps {
  editor: Editor | null;
  onSetLink?: () => void;
  onLinkActive?: () => void;
}

export interface LinkMainProps {
  url: string;
  setUrl: React.Dispatch<React.SetStateAction<string>>;
  setLink: () => void;
  removeLink: () => void;
  isActive: boolean;
  target: string;
  setTarget: React.Dispatch<React.SetStateAction<string>>;
  text: string;
  setText: React.Dispatch<React.SetStateAction<string>>;
}

export const useLinkHandler = (props: LinkHandlerProps) => {
  const { editor, onSetLink, onLinkActive } = props;
  const [url, setUrl] = React.useState<string>("");
  const [target, setTarget] = React.useState<string>("_blank");
  const [text, setText] = React.useState<string>("");

  React.useEffect(() => {
    if (!editor) return;

    // Get URL, target, and text immediately on mount
    const { href, target: linkTarget } = editor.getAttributes("link");
    const selectedText = editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to);

    if (editor.isActive("link") && !url) {
      setUrl(href || "");
      setTarget(linkTarget || "_blank");
      setText(selectedText || "");
      onLinkActive?.();
    }
  }, [editor, onLinkActive, url]);

  React.useEffect(() => {
    if (!editor) return;

    const updateLinkState = () => {
      const { href, target: linkTarget } = editor.getAttributes("link");
      const selectedText = editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to);
      setUrl(href || "");
      setTarget(linkTarget || "_blank");
      setText(selectedText || "");

      if (editor.isActive("link") && !url) {
        onLinkActive?.();
      }
    };

    editor.on("selectionUpdate", updateLinkState);
    return () => {
      editor.off("selectionUpdate", updateLinkState);
    };
  }, [editor, onLinkActive, url]);

  const setLink = React.useCallback(() => {
    if (!url || !editor) return;

    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to);
    const linkText = text || selectedText || url;

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .insertContent({
        type: "text",
        text: linkText,
        marks: [{ type: "link", attrs: { href: url, target } }],
      })
      .run();

    onSetLink?.();
  }, [editor, onSetLink, url, target, text]);

  const removeLink = React.useCallback(() => {
    if (!editor) return;
    editor.chain().focus().unsetMark("link", { extendEmptyMarkRange: true }).setMeta("preventAutolink", true).run();
    setUrl("");
    setTarget("_blank");
    setText("");
  }, [editor]);

  return {
    url,
    setUrl,
    setLink,
    removeLink,
    isActive: editor?.isActive("link") || false,
    target,
    setTarget,
    text,
    setText,
  };
};

export const LinkButton = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, children, ...props }, ref) => {
  const { t } = useTranslation("common");
  return (
    <Button
      type="button"
      className={className}
      data-style="ghost"
      role="button"
      aria-label={t("editor.link")}
      tooltip={t("editor.link")}
      ref={ref}
      {...props}
    >
      {children || <LinkIcon className="tiptap-button-icon" />}
    </Button>
  );
});

export const LinkContent: React.FC<{
  editor?: Editor | null;
  size?: ButtonSize;
}> = ({ editor: providedEditor, size = "base" }) => {
  const editor = useTiptapEditor(providedEditor);

  const linkHandler = useLinkHandler({
    editor: editor,
  });

  return <LinkMain {...linkHandler} size={size} />;
};

const LinkMain: React.FC<LinkMainProps & { size?: ButtonSize }> = ({
  url,
  setUrl,
  setLink,
  removeLink,
  isActive,
  target,
  setTarget,
  text,
  setText,
}) => {
  const { t } = useTranslation("common");

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setLink();
    }
  };

  return (
    <div className="w-80 space-y-4 p-1">
      <div className="space-y-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-foreground">{t("editor.linkTextLabel")}</label>
          <input
            type="text"
            placeholder={t("editor.linkTextPlaceholder")}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            className="w-full rounded-md border-0 bg-muted/50 px-3 py-2 text-sm placeholder:text-muted-foreground focus:bg-background focus:outline-hidden focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-foreground">{t("editor.linkUrlLabel")}</label>
          <input
            type="url"
            placeholder={t("editor.linkUrlPlaceholder")}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            className="w-full rounded-md border-0 bg-muted/50 px-3 py-2 text-sm placeholder:text-muted-foreground focus:bg-background focus:outline-hidden focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </div>
      <div className="flex items-center space-x-1 text-sm text-muted-foreground">
        <Switch
          id="link-open-new-tab"
          size="sm"
          className="w-7!"
          checked={target === "_blank"}
          onCheckedChange={() => setTarget(target === "_blank" ? "_self" : "_blank")}
          aria-label={t("editor.openInNewWindow")}
        />
        <label htmlFor="link-open-new-tab">{t("editor.openInNewWindow")}</label>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={setLink}
            disabled={!url && !isActive}
            className="rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground transition-all hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("editor.apply")}
          </button>
          <button
            type="button"
            onClick={() => window.open(url, target)}
            disabled={!url && !isActive}
            className="rounded-md p-1.5 text-muted-foreground transition-all hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            title={t("editor.openLink")}
          >
            <ExternalLinkIcon className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={removeLink}
            disabled={!url && !isActive}
            className="rounded-md p-1.5 text-muted-foreground transition-all hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
            title={t("editor.removeLink")}
          >
            <TrashIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export interface LinkPopoverProps extends Omit<ButtonProps, "type"> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor | null;
  /**
   * Whether to hide the link popover.
   * @default false
   */
  hideWhenUnavailable?: boolean;
  /**
   * Callback for when the popover opens or closes.
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * Whether to automatically open the popover when a link is active.
   * @default true
   */
  autoOpenOnLinkActive?: boolean;
  /**
   * Whether the popover is in a modal.
   * @default false
   */
  isInModal?: boolean;
}

export function LinkPopover({
  editor: providedEditor,
  hideWhenUnavailable = false,
  onOpenChange,
  autoOpenOnLinkActive = true,
  isInModal = false,
  ...props
}: LinkPopoverProps) {
  const editor = useTiptapEditor(providedEditor);

  const linkInSchema = isMarkInSchema("link", editor);

  const [isOpen, setIsOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const onSetLink = () => {
    setIsOpen(false);
  };

  const onLinkActive = () => setIsOpen(autoOpenOnLinkActive);

  const linkHandler = useLinkHandler({
    editor: editor,
    onSetLink,
    onLinkActive,
  });

  const isDisabled = React.useMemo(() => {
    if (!editor) return true;
    if (editor.isActive("codeBlock")) return true;
    return !editor.can().setLink?.({ href: "" });
  }, [editor]);

  const canSetLink = React.useMemo(() => {
    if (!editor) return false;
    try {
      return editor.can().setMark("link");
    } catch {
      return false;
    }
  }, [editor]);

  const isActive = editor?.isActive("link") ?? false;

  const handleOnOpenChange = React.useCallback(
    (nextIsOpen: boolean) => {
      setIsOpen(nextIsOpen);
      onOpenChange?.(nextIsOpen);
    },
    [onOpenChange],
  );

  const show = React.useMemo(() => {
    if (!linkInSchema) {
      return false;
    }

    if (hideWhenUnavailable) {
      if (isNodeSelection(editor?.state.selection) || !canSetLink) {
        return false;
      }
    }

    return true;
  }, [linkInSchema, hideWhenUnavailable, editor, canSetLink]);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  // When inside a Radix Dialog, portal into the dialog container instead of
  // document.body so the popover stays within the dialog's focus trap and is
  // not marked inert by Radix.
  const dialogRoot =
    isInModal && triggerRef.current
      ? ((triggerRef.current.closest('[role="dialog"]') as HTMLElement | null) ?? undefined)
      : undefined;

  return (
    <Popover open={isOpen} onOpenChange={handleOnOpenChange}>
      <PopoverTrigger asChild>
        <LinkButton
          ref={triggerRef}
          disabled={isDisabled}
          data-active-state={isActive ? "on" : "off"}
          data-disabled={isDisabled}
          {...props}
        />
      </PopoverTrigger>

      <PopoverContent
        style={{ zIndex: isInModal ? 9999 : undefined }}
        {...(dialogRoot ? { portalProps: { root: dialogRoot } } : {})}
      >
        <LinkMain {...linkHandler} />
      </PopoverContent>
    </Popover>
  );
}

LinkButton.displayName = "LinkButton";
